import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { Settings, Save, AlertCircle, TestTube, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const DEFAULT_CONFIG = {
  categoryWeights: {
    'Device Status': 40,
    'Display Status': 30,
    'Functional QC': 20,
    'Device Cosmetics': 10
  },
  severityPoints: {
    'NTF': 0,
    'Low': -5,
    'Medium': -10,
    'High': -20,
    'Critical_L1': -50,
    'Critical_L4': -100
  },
  ruleOverrides: [
    { condition: 'ANY Critical_L4', result: 'D or E', enabled: true },
    { condition: 'Dead Phone (DOA)', result: 'E', enabled: true },
    { condition: '3+ High severity issues', result: 'Maximum C', enabled: true },
    { condition: 'Finance/Account Lock', result: 'E', enabled: true }
  ],
  gradeThresholds: [
    { min: 0, max: -20, grade: 'A+', color: 'bg-green-600' },
    { min: -21, max: -40, grade: 'A', color: 'bg-green-500' },
    { min: -41, max: -60, grade: 'A-', color: 'bg-green-400' },
    { min: -61, max: -80, grade: 'B+', color: 'bg-blue-500' },
    { min: -81, max: -100, grade: 'B', color: 'bg-blue-400' },
    { min: -101, max: -120, grade: 'B-', color: 'bg-blue-300' },
    { min: -121, max: -140, grade: 'C+', color: 'bg-yellow-500' },
    { min: -141, max: -160, grade: 'C', color: 'bg-yellow-400' },
    { min: -161, max: -180, grade: 'C-', color: 'bg-yellow-300' },
    { min: -181, max: -200, grade: 'D', color: 'bg-orange-500' },
    { min: -201, max: -9999, grade: 'E', color: 'bg-red-600' }
  ]
};

const PQCGradingConfig = ({ user, onLogout }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/pqc/grading-config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data) {
        setConfig(response.data);
      }
    } catch (error) {
      console.log('No saved config found, using defaults');
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  };

  const updateCategoryWeight = (category, value) => {
    const newWeights = { ...config.categoryWeights, [category]: parseInt(value) };
    setConfig({ ...config, categoryWeights: newWeights });
    setHasChanges(true);
  };

  const updateSeverityPoint = (severity, value) => {
    const newPoints = { ...config.severityPoints, [severity]: parseInt(value) };
    setConfig({ ...config, severityPoints: newPoints });
    setHasChanges(true);
  };

  const toggleRule = (index) => {
    const newRules = [...config.ruleOverrides];
    newRules[index].enabled = !newRules[index].enabled;
    setConfig({ ...config, ruleOverrides: newRules });
    setHasChanges(true);
  };

  const updateGradeThreshold = (index, field, value) => {
    const newThresholds = [...config.gradeThresholds];
    newThresholds[index][field] = parseInt(value);
    setConfig({ ...config, gradeThresholds: newThresholds });
    setHasChanges(true);
  };

  const calculateTestGrade = () => {
    // Simple test calculation
    const testScore = -45; // Example score
    const grade = config.gradeThresholds.find(t => testScore >= t.min && testScore <= t.max);
    setTestResults({
      score: testScore,
      grade: grade?.grade || 'Unknown',
      color: grade?.color || 'bg-gray-500'
    });
  };

  const saveConfiguration = async () => {
    if (!window.confirm('Save PQC Grading Configuration?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/pqc/grading-config`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('✅ Configuration saved successfully!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('❌ Failed to save configuration');
    }
  };

  const totalWeight = Object.values(config.categoryWeights).reduce((sum, val) => sum + val, 0);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading PQC Grading Configuration...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
              <Settings className="w-8 h-8 mr-3" />
              PQC Grading Configuration
            </h1>
            <p className="text-gray-600 mt-1">
              Configure hybrid grading system for Pre-QC (Buyback Pricing)
            </p>
          </div>
          <button
            onClick={saveConfiguration}
            disabled={!hasChanges}
            className={`flex items-center px-6 py-3 rounded-lg font-semibold ${
              hasChanges
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save className="w-5 h-5 mr-2" />
            Save Configuration
            {hasChanges && <span className="ml-2 px-2 py-1 bg-red-500 text-xs rounded-full">*</span>}
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-2">Hybrid Grading System:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Category Weights:</strong> Different importance for different QC categories</li>
                <li><strong>Point System:</strong> Each severity level deducts points from total score</li>
                <li><strong>Rule Overrides:</strong> Critical conditions automatically set specific grades</li>
                <li><strong>Final Grade:</strong> Calculated score maps to grade (A+ to E) for buyback pricing</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Category Weights */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            Category Weights {totalWeight !== 100 && <span className="text-red-600 text-sm">(Total must be 100%)</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(config.categoryWeights).map(([category, weight]) => (
              <div key={category} className="border-2 border-gray-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {category}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={weight}
                    onChange={(e) => updateCategoryWeight(category, e.target.value)}
                    className="flex-1"
                  />
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => updateCategoryWeight(category, e.target.value)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded text-center font-bold"
                    min="0"
                    max="100"
                  />
                  <span className="text-lg font-bold text-gray-600">%</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 rounded" style={{ backgroundColor: totalWeight === 100 ? '#d4edda' : '#f8d7da' }}>
            <p className="text-center font-bold" style={{ color: totalWeight === 100 ? '#155724' : '#721c24' }}>
              Total Weight: {totalWeight}% {totalWeight === 100 ? '✅' : '❌'}
            </p>
          </div>
        </div>

        {/* Severity Points */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            Severity Point System
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(config.severityPoints).map(([severity, points]) => (
              <div key={severity} className="border-2 border-gray-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {severity}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={points}
                    onChange={(e) => updateSeverityPoint(severity, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-center font-bold"
                    step="5"
                  />
                  <span className="text-sm text-gray-600">points</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rule Overrides */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            Rule-Based Overrides
          </h2>
          <div className="space-y-3">
            {config.ruleOverrides.map((rule, index) => (
              <div key={index} className="border-2 border-gray-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={() => toggleRule(index)}
                    className="w-5 h-5"
                  />
                  <div>
                    <p className="font-semibold text-gray-800">IF: {rule.condition}</p>
                    <p className="text-sm text-gray-600">THEN: Grade = <span className="font-bold text-red-600">{rule.result}</span></p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded text-xs font-bold ${
                  rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {rule.enabled ? 'ACTIVE' : 'DISABLED'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Grade Thresholds */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            Grade Thresholds (Score to Grade Mapping)
          </h2>
          <div className="space-y-3">
            {config.gradeThresholds.map((threshold, index) => (
              <div key={index} className="border-2 border-gray-200 rounded-lg p-4 flex items-center gap-4">
                <span className={`px-4 py-2 ${threshold.color} text-white font-bold rounded text-lg min-w-[60px] text-center`}>
                  {threshold.grade}
                </span>
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="number"
                    value={threshold.min}
                    onChange={(e) => updateGradeThreshold(index, 'min', e.target.value)}
                    className="w-24 px-3 py-2 border border-gray-300 rounded text-center"
                  />
                  <span className="text-gray-600">to</span>
                  <input
                    type="number"
                    value={threshold.max}
                    onChange={(e) => updateGradeThreshold(index, 'max', e.target.value)}
                    className="w-24 px-3 py-2 border border-gray-300 rounded text-center"
                  />
                  <span className="text-gray-600">points</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Test Calculator */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg shadow-md p-6 border-2 border-purple-200">
          <h2 className="text-xl font-bold mb-4 flex items-center" style={{ color: ZOBOX_BLUE }}>
            <TestTube className="w-6 h-6 mr-2" />
            Grade Calculator (Test)
          </h2>
          <div className="bg-white rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-600 mb-3">
              Select QC parameters to calculate grade based on current configuration
            </p>
            <button
              onClick={calculateTestGrade}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
            >
              Calculate Test Grade
            </button>
          </div>
          
          {testResults && (
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Score:</p>
                  <p className="text-3xl font-bold text-gray-800">{testResults.score} points</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Calculated Grade:</p>
                  <span className={`inline-block px-6 py-3 ${testResults.color} text-white font-bold rounded text-2xl`}>
                    {testResults.grade}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Formula Explanation */}
        <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
          <h3 className="font-bold text-lg mb-3" style={{ color: ZOBOX_BLUE }}>Calculation Formula:</h3>
          <div className="space-y-2 text-sm font-mono bg-white p-4 rounded">
            <p>1. For each QC parameter issue:</p>
            <p className="ml-4">Score = Severity Points × Category Weight%</p>
            <p>2. Sum all issue scores = Total Score</p>
            <p>3. Apply Rule Overrides (if any match)</p>
            <p>4. Map Total Score to Grade using thresholds</p>
          </div>
        </div>

        {/* Floating Save Button */}
        {hasChanges && (
          <div className="fixed bottom-6 right-6 z-50">
            <button
              onClick={saveConfiguration}
              className="flex items-center px-6 py-4 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 font-bold text-lg"
            >
              <Save className="w-6 h-6 mr-2" />
              Save All Changes
              <span className="ml-2 px-2 py-1 bg-red-500 text-xs rounded-full">Unsaved</span>
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PQCGradingConfig;
